<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'

const props = defineProps<{
  config: {
    series: { name: string; data: { date: string; value: number }[] }[]
    xAxisLabel: string
    yAxisLabel: string
  }
}>()

const chartContainer = ref<HTMLDivElement | null>(null)
let am5Root: am5.Root | null = null

const renderChart = () => {
  if (chartContainer.value) {
    if (am5Root) {
      am5Root.dispose()
    }
    am5Root = am5.Root.new(chartContainer.value)
    // FIX: Capture root in a local variable to ensure strict type safety for subsequent calls
    const root = am5Root

    root.setThemes([am5themes_Animated.new(root)])

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
      }),
    )

    const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}))
    cursor.lineY.set('visible', false)

    // Asse X (Data)
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.2,
        baseInterval: { timeUnit: 'month', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 50,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      }),
    )
    // Ruotiamo le etichette per leggibilità
    xAxis.get('renderer').labels.template.setAll({
      rotation: -45,
      centerY: am5.p50,
      centerX: am5.p100,
      paddingRight: 10,
    })

    // Asse Y (Valore)
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      }),
    )

    // FIX: Set axis title by adding a Label to its children, not via .set('title', ...)
    if (props.config.yAxisLabel) {
      yAxis.children.unshift(
        am5.Label.new(root, {
          text: props.config.yAxisLabel,
          rotation: -90,
          y: am5.p50,
          centerX: am5.p50,
          paddingRight: 15, // Adjusted padding for better visibility
        }),
      )
    }

    // Aggiungiamo le serie (linee)
    props.config.series.forEach((s) => {
      const series = chart.series.push(
        am5xy.LineSeries.new(root, {
          // FIX: Use local 'root'
          name: s.name,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'value',
          valueXField: 'date',
          tooltip: am5.Tooltip.new(root, {
            // FIX: Use local 'root'
            labelText: '{name}: {valueY}',
          }),
        }),
      )

      // Convertiamo le date da stringa a oggetto Date
      const seriesData = s.data.map((d) => ({
        ...d,
        date: new Date(d.date).getTime(),
      }))

      series.data.setAll(seriesData)
      series.appear(1000)
    })

    // Legenda
    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50,
      }),
    )
    legend.data.setAll(chart.series.values)

    chart.appear(1000, 100)
  }
}

onMounted(renderChart)
watch(() => props.config, renderChart, { deep: true })
onBeforeUnmount(() => am5Root?.dispose())
</script>

<template>
  <div ref="chartContainer" class="w-full h-[500px]"></div>
</template>
